import React from 'react';
import { Trophy, Plus, FilterX, Sparkles, Compass, Shield } from 'lucide-react';

interface TournamentEmptyIllustrationProps {
  title?: string;
  description?: string;
  selectedSport?: string;
  onResetFilters?: () => void;
  onCreateTournament?: () => void;
}

export const TournamentEmptyIllustration: React.FC<TournamentEmptyIllustrationProps> = ({
  title = "No Tournament Brackets Active",
  description = "No court cups, elimination brackets, or league tournaments match your current filters. Be the first commissioner to host an event or adjust your search!",
  selectedSport = "all",
  onResetFilters,
  onCreateTournament,
}) => {
  return (
    <div className="relative w-full p-8 md:p-12 text-center bg-gradient-to-b from-indigo-950/90 via-indigo-900/60 to-indigo-950/90 rounded-[2.5rem] border border-white/15 shadow-2xl overflow-hidden space-y-6 group">
      {/* Ambient Backdrop Glow & Grid Lines */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-lime-400/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-60 h-60 bg-lime-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute -bottom-24 -right-24 w-60 h-60 bg-amber-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* CUSTOM SVG VECTOR ILLUSTRATION FOR TOURNAMENTS */}
      <div className="relative w-48 h-48 mx-auto flex items-center justify-center">
        {/* Outer Glowing Energy Rings */}
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-lime-400/30 animate-[spin_20s_linear_infinite]" />
        <div className="absolute inset-3 rounded-full border border-white/10 bg-indigo-950/80 backdrop-blur-md shadow-2xl flex items-center justify-center" />

        {/* Stadium Court Brackets Vector Artwork */}
        <svg
          viewBox="0 0 200 200"
          className="w-full h-full relative z-10 drop-shadow-[0_10px_25px_rgba(163,230,53,0.3)]"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Background Court Field & Lines */}
          <rect x="25" y="45" width="150" height="110" rx="16" fill="#090d26" stroke="#ffffff" strokeOpacity="0.12" strokeWidth="2" />
          <line x1="100" y1="45" x2="100" y2="155" stroke="#a3e635" strokeOpacity="0.25" strokeWidth="2" strokeDasharray="4 4" />
          <circle cx="100" cy="100" r="24" stroke="#a3e635" strokeOpacity="0.3" strokeWidth="2" />

          {/* Tournament Bracket Tree Paths (Left Side) */}
          <path d="M 35 65 H 55 V 85 H 35" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="55" y1="75" x2="70" y2="75" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 35 115 H 55 V 135 H 35" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="55" y1="125" x2="70" y2="125" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 70 75 V 125" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="70" y1="100" x2="82" y2="100" stroke="#a3e635" strokeWidth="3" strokeLinecap="round" />

          {/* Tournament Bracket Tree Paths (Right Side) */}
          <path d="M 165 65 H 145 V 85 H 165" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="145" y1="75" x2="130" y2="75" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 165 115 H 145 V 135 H 165" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="145" y1="125" x2="130" y2="125" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M 130 75 V 125" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="130" y1="100" x2="118" y2="100" stroke="#a3e635" strokeWidth="3" strokeLinecap="round" />

          {/* Center Championship Trophy Emblem */}
          <g transform="translate(80, 75)">
            <path
              d="M 8 5 H 32 C 32 5 32 25 20 28 C 8 25 8 5 8 5 Z"
              fill="url(#trophyGrad)"
              stroke="#f59e0b"
              strokeWidth="2"
            />
            <path d="M 5 8 H 8 V 16 H 5 C 3.3 16 2 14.7 2 13 V 11 C 2 9.3 3.3 8 5 8 Z" fill="#fbbf24" stroke="#d97706" />
            <path d="M 35 8 H 32 V 16 H 35 C 36.7 16 38 14.7 38 13 V 11 C 38 9.3 36.7 8 35 8 Z" fill="#fbbf24" stroke="#d97706" />
            <rect x="17" y="28" width="6" height="10" fill="#f59e0b" />
            <rect x="12" y="38" width="16" height="6" rx="2" fill="#d97706" />
            {/* Crown Star Accent */}
            <circle cx="20" cy="16" r="4" fill="#ffffff" />
            <path d="M 20 12 L 21.2 14.8 L 24 15.2 L 22 17 L 22.5 19.8 L 20 18.3 L 17.5 19.8 L 18 17 L 16 15.2 L 18.8 14.8 Z" fill="#fbbf24" />
          </g>

          {/* Floating Sparkle Stars */}
          <path d="M 30 35 L 32 40 L 37 42 L 32 44 L 30 49 L 28 44 L 23 42 L 28 40 Z" fill="#a3e635" />
          <path d="M 170 35 L 171.5 39 L 175.5 40.5 L 171.5 42 L 170 46 L 168.5 42 L 164.5 40.5 L 168.5 39 Z" fill="#38bdf8" />
          <path d="M 100 22 L 102 27 L 107 29 L 102 31 L 100 36 L 98 31 L 93 29 L 98 27 Z" fill="#fbbf24" />

          {/* Gradient Definitions */}
          <defs>
            <linearGradient id="trophyGrad" x1="8" y1="5" x2="32" y2="38" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="50%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#b45309" />
            </linearGradient>
          </defs>
        </svg>

        {/* Floating Trophy Badge Indicator */}
        <div className="absolute -top-1 -right-1 bg-lime-400 text-black font-black text-[10px] px-2.5 py-1 rounded-xl shadow-lg flex items-center space-x-1 border border-lime-300 animate-bounce">
          <Sparkles className="w-3 h-3 fill-black" />
          <span>Bracket Ready</span>
        </div>
      </div>

      {/* Copy & Status Message */}
      <div className="space-y-2 max-w-md mx-auto relative z-10">
        <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-lime-400/10 border border-lime-400/30 text-lime-300 text-[10px] font-black uppercase tracking-wider font-mono">
          <Shield className="w-3 h-3 text-lime-400" />
          <span>Sport Category: {selectedSport.toUpperCase()}</span>
        </div>

        <h3 className="text-lg md:text-xl font-black italic uppercase tracking-wide text-white drop-shadow-md">
          {title}
        </h3>
        <p className="text-xs md:text-sm text-indigo-200/80 font-medium leading-relaxed">
          {description}
        </p>
      </div>

      {/* Interactive Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 max-w-sm mx-auto relative z-10">
        {onResetFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            className="w-full sm:w-auto px-5 py-2.5 bg-indigo-900/90 hover:bg-indigo-800 text-lime-300 border border-lime-400/40 text-xs font-black italic uppercase rounded-2xl transition shadow-lg flex items-center justify-center space-x-2 active:scale-95"
          >
            <FilterX className="w-4 h-4 text-lime-400" />
            <span>Reset All Filters</span>
          </button>
        )}

        {onCreateTournament && (
          <button
            type="button"
            onClick={onCreateTournament}
            className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-lime-400 via-emerald-400 to-lime-400 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/25 hover:scale-105 transition flex items-center justify-center space-x-2 active:scale-95"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            <span>Create Tournament Bracket</span>
          </button>
        )}
      </div>
    </div>
  );
};
